/**
 * excelImport.js
 *
 * Parses the Changes and Reference Accounts sheets from a BridgeFirst Excel export
 * back into the shape expected by POST /changes/.
 *
 * These sheets are produced by excelExport.js. The column names here must match
 * the labels defined there.
 */

import { parse, format } from 'date-fns';

// ─── Helpers ────────────────────────────────────────────────────────────────

/**
 * Parse a "Jan 2024 - Dec 2024" date range string back into an array of
 * ISO date strings: ["2024-01-01", "2024-12-01"].
 */
function parseDateRangeString(str) {
  if (!str || typeof str !== 'string') return [];
  const parts = str.split(' - ');
  if (parts.length < 2) return [];
  try {
    const REF = new Date(2000, 0, 1);
    const start = parse(parts[0].trim(), 'MMM yyyy', REF);
    const end   = parse(parts[1].trim(), 'MMM yyyy', REF);
    if (isNaN(start.getTime()) || isNaN(end.getTime())) return [];
    return [format(start, 'yyyy-MM-dd'), format(end, 'yyyy-MM-dd')];
  } catch {
    return [];
  }
}

/**
 * Parse a comma-separated GL key string back into an array.
 * "1-10-1001, 1-10-1002" → ["1-10-1001", "1-10-1002"]
 */
function parseCommaSeparated(str) {
  if (!str || typeof str !== 'string') return [];
  return str.split(',').map(s => s.trim()).filter(Boolean);
}

function parseBool(val) {
  if (typeof val === 'boolean') return val;
  if (typeof val === 'string') return val.trim().toUpperCase() === 'TRUE';
  return false;
}

// ─── Sheet parsers ───────────────────────────────────────────────────────────

/**
 * Parse the Changes sheet rows into change objects ready for POST /changes/.
 * Reference accounts are NOT attached here — call parseReferenceAccountsSheet
 * afterwards to attach them.
 *
 * @param {Array<Object>} rows - rows from the Changes sheet
 * @returns {Array<Object>} change objects (without reference_accounts)
 */
export function parseChangesSheet(rows) {
  if (!Array.isArray(rows)) return [];

  return rows
    .filter(row => row['Change Label'] && String(row['Change Label']).trim())
    .map(row => ({
      change_label:          String(row['Change Label'] ?? '').trim(),
      // Preserve original casing — backend stores 'absoluteValue', 'percentage', etc.
      function_operator:     String(row['Function Operator'] ?? '').trim(),
      operator_value:        String(row['Operator Value'] ?? '0').trim(),
      date_range:            parseDateRangeString(row['Date Range']),
      date_range_select:     String(row['Date Range Select'] ?? 'custom').trim(),
      selected_accounts:     parseCommaSeparated(row['Selected Accounts']),
      parent_change:         String(row['Parent Change'] ?? '').trim(),
      is_dominant_change:    parseBool(row['Is Dominant Change']),
      is_stat_driver:        parseBool(row['Is Stat Driver']),
      is_master_stat_change: parseBool(row['Is Master Stat Change']),
      // Use empty string instead of null — schema has Str() without allow_none=True
      ref_account_formula:   String(row['Ref Account Formula'] ?? '').trim(),
      user_note:             String(row['User Note'] ?? '').trim() || '',
      // These will be filled in at import time
      reference_accounts:    [],
    }));
}

/**
 * Parse the Reference Accounts sheet and attach each ref account to its
 * corresponding change (matched by Change Label).
 *
 * @param {Array<Object>} rows - rows from the Reference Accounts sheet
 * @param {Array<Object>} changes - output of parseChangesSheet
 * @returns {Array<Object>} the same changes array with reference_accounts populated
 */
export function parseReferenceAccountsSheet(rows, changes) {
  if (!Array.isArray(rows) || rows.length === 0) return changes;

  // Build a label → change index map for O(1) lookup
  const labelMap = new Map();
  changes.forEach((c, i) => labelMap.set(c.change_label, i));

  for (const row of rows) {
    const label = String(row['Change Label'] ?? '').trim();
    const idx = labelMap.get(label);
    if (idx === undefined) continue; // orphaned ref row — skip

    changes[idx].reference_accounts.push({
      glKey:          String(row['GL Key']        ?? '').trim(),
      rowLetter:      String(row['Row Letter']    ?? '').trim(),
      is_fixed_value: parseBool(row['Is Fixed Value']),
      is_stat_driver: parseBool(row['Is Stat Driver']),
    });
  }

  return changes;
}
